import React from 'react';
import clsx from 'clsx';
import {Icon, faBookMedical, type IconDefinition} from '@netx/core-icons';
import {
	Avatar,
	ListItem,
	ListItemAvatar,
	ListItemText,
	ListItemIcon,
} from '@mui/material';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import ColoredAvatar from '@netx/core-components/ColoredAvatar';
import {
	autoListFieldOptionClasses,
	getAutoListFieldOptionUtilityClass,
	type AutoListFieldOptionClasses,
	AUTO_LIST_FIELD_OPTION_CLASS_NAME,
} from './autoListFieldOptionClasses';

type AutoListFieldOptionOwnerState = WithClasses<AutoListFieldOptionClasses>;

const useUtilityClasses = (ownerState: AutoListFieldOptionOwnerState) => {
	const {classes} = ownerState;

	const slots = {
		root: ['root'],
		avatar: ['avatar'],
		coloredAvatar: ['coloredAvatar'],
		listItemIcon: ['listItemIcon'],
	};

	return composeClasses(slots, getAutoListFieldOptionUtilityClass, classes);
};

const AutoListFieldOptionRoot = styled(ListItem, {
	name: AUTO_LIST_FIELD_OPTION_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({}));

const AutoListFieldOptionAvatar = styled(Avatar, {
	name: AUTO_LIST_FIELD_OPTION_CLASS_NAME,
	slot: 'Avatar',
})(({theme}) => ({
	flex: '0 0 auto',
	marginRight: 16,
	height: 32,
	width: 32,
	fontSize: 18,
}));

const AutoListFieldOptionColoredAvatar = styled(ColoredAvatar, {
	name: AUTO_LIST_FIELD_OPTION_CLASS_NAME,
	slot: 'Avatar',
})(({theme}) => ({
	flex: '0 0 auto',
	marginRight: 16,
	height: 32,
	width: 32,
	fontSize: 18,
}));

const AutoListFieldOptionListItemIcon = styled(ListItemIcon)(({theme}) => ({
	minWidth: 36,
}));

export interface AutoListOptionProps<
	T extends string,
> extends WithClasses<AutoListFieldOptionClasses> {
	avatar?: boolean;
	icon?: IconDefinition;
	value: T;
	secondary?: React.ReactNode;
	uncontrolled?: boolean;
}

export const AutoListFieldOptionPrimitive = <T extends string>({
	classes: _classes,
	className,
	avatar,
	icon,
	secondary,
	uncontrolled,
	value,
	...props
}: AutoListOptionProps<T>) => {
	const ownerState: AutoListFieldOptionOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<AutoListFieldOptionRoot
			{...props}
			className={clsx(classes.root, className)}
			title={value}
		>
			{avatar ? (
				<ListItemAvatar>
					{icon ? (
						<AutoListFieldOptionAvatar className={classes.avatar}>
							<Icon icon={icon} />
						</AutoListFieldOptionAvatar>
					) : (
						<AutoListFieldOptionColoredAvatar
							className={classes.avatar}
							label={value}
						/>
					)}
				</ListItemAvatar>
			) : null}
			{uncontrolled ? (
				<AutoListFieldOptionListItemIcon>
					<Icon icon={faBookMedical} />
				</AutoListFieldOptionListItemIcon>
			) : null}
			<ListItemText
				primary={value}
				secondary={secondary}
				primaryTypographyProps={{noWrap: true}}
			/>
		</AutoListFieldOptionRoot>
	);
};

export const AutoListFieldOption = AutoListFieldOptionPrimitive;

export default AutoListFieldOption;
